import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { Calculator, Check, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CostingCard = forwardRef(function CostingCard(
  { ready, stale, costingForm, setCostingForm, onCalculate, calculating, costing, onAddToCart, adding, inCart, cartPath },
  ref,
) {
  return (
    <section ref={ref} className="bg-card border border-border rounded-2xl p-5 sm:p-6 scroll-mt-24" data-testid="costing-cart-section">
      <div className="flex items-start gap-4 mb-5">
        <span className="h-12 w-12 rounded-full bg-accent/10 text-accent flex items-center justify-center shrink-0">
          <Calculator className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div>
          <h2 className="font-serif text-xl text-foreground">Costing &amp; Cart</h2>
          <p className="text-sm text-muted-foreground">Price this design, then add it to the project cart.</p>
        </div>
      </div>

      {!ready ? (
        <p className="text-sm text-muted-foreground">Choose a fabric and save the design first.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-5 items-end">
          <div className="space-y-4">
            {stale && (
              <p className="text-xs rounded-lg bg-warning/15 text-[hsl(41_60%_32%)] px-3 py-2" data-testid="costing-stale-note">
                You've changed the design since it was saved. Save it again so the price matches.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fabric rate / meter (₹)</Label>
                <Input data-testid="costing-rate-input" type="number" value={costingForm.fabricRatePerMeter} onChange={(e) => setCostingForm({ ...costingForm, fabricRatePerMeter: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-xs">Accessories count</Label>
                <Input data-testid="costing-accessories-input" type="number" value={costingForm.accessoriesCount} onChange={(e) => setCostingForm({ ...costingForm, accessoriesCount: Number(e.target.value) })} />
              </div>
            </div>
            <Button data-testid="calculate-costing-button" variant="outline" disabled={stale || calculating} onClick={onCalculate}>
              {calculating ? "Calculating…" : "Calculate Cost"}
            </Button>
          </div>

          {costing && (
            <div className="rounded-xl bg-secondary/70 p-4" data-testid="costing-breakdown">
              <p className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">Estimated Price</p>
              <p className="font-serif text-3xl text-foreground mt-1 tabular-nums">
                ₹{costing.finalPrice?.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <Button
                data-testid="add-to-cart-button"
                disabled={stale || adding}
                onClick={onAddToCart}
                className="mt-4 w-full bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                <ShoppingCart className="h-4 w-4 mr-2" /> {inCart ? "Add Again to Cart" : "Add to Project Cart"}
              </Button>
              {inCart && cartPath && (
                <p className="mt-2.5 text-sm text-success flex items-center gap-1.5" data-testid="added-to-cart-note">
                  <Check className="h-4 w-4" /> In project cart ·
                  <Link to={cartPath} className="text-accent hover:underline font-medium" data-testid="view-project-cart-link">
                    View project cart →
                  </Link>
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
});

export default CostingCard;
